import type { UserRole } from 'api/types/auth.types';
import { useAuthStore } from 'features/auth/stores/authStore';

/** Normalize legacy lowercase roles from old localStorage to the current PascalCase format. */
function normalizeRole(role: string | undefined): UserRole | undefined {
  if (!role) return undefined;
  const map: Record<string, UserRole> = {
    owner: 'Owner',
    admin: 'Admin',
    projectmanager: 'ProjectManager',
    member: 'TeamMember',
    teammember: 'TeamMember',
    viewer: 'TeamMember',
  };
  return (map[role.toLowerCase()] ?? role) as UserRole;
}

const isAtLeastAdmin = (role: UserRole | undefined): boolean =>
  role === 'Owner' || role === 'Admin';

const isAtLeastProjectManager = (role: UserRole | undefined): boolean =>
  role === 'Owner' || role === 'Admin' || role === 'ProjectManager';

export function usePermissions() {
  const rawRole = useAuthStore((s) => s.user?.role);
  const role = normalizeRole(rawRole);

  return {
    // Role flags
    isOwner: role === 'Owner',
    isAdmin: role === 'Admin',
    isProjectManager: role === 'ProjectManager',
    isTeamMember: role === 'TeamMember',

    // Clients — view: ProjectManager+, create/edit/delete: Admin+
    canViewClients: isAtLeastProjectManager(role),
    canCreateClient: isAtLeastAdmin(role),
    canEditClient: isAtLeastAdmin(role),
    canDeleteClient: isAtLeastAdmin(role),

    // Projects — create/edit: ProjectManager+, delete: Admin+
    canCreateProject: isAtLeastProjectManager(role),
    canEditProject: isAtLeastProjectManager(role),
    canDeleteProject: isAtLeastAdmin(role),
    canAssignProjectMembers: isAtLeastProjectManager(role),

    // Team — Admin+
    canInviteMember: isAtLeastAdmin(role),
    canEditMemberRole: isAtLeastAdmin(role),
    canDeactivateMember: isAtLeastAdmin(role),
    canSetBillableRate: isAtLeastAdmin(role),

    // Tags — ProjectManager+
    canCreateTag: isAtLeastProjectManager(role),

    // Reports — edit/delete entries: ProjectManager+
    canEditReportEntries: isAtLeastProjectManager(role),

    // Reports — view all users' data (vs. only own): ProjectManager+
    canViewAllUsersReports: isAtLeastProjectManager(role),
    
    // Reports — create entries on behalf of other users: Admin+
    canCreateEntriesForOthers: isAtLeastAdmin(role),
  };
}
